import React, { Component, ReactNode } from 'react';
import { MessageCircle } from 'lucide-react';
import { useFrontChat } from '../providers/FrontChatProvider';

// Error boundary for chat functionality
class FrontChatErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('FrontChat error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <button
          className="flex cursor-not-allowed items-center gap-2 px-3 py-2 text-sm text-gray-400"
          disabled
          title="Chat service temporarily unavailable"
        >
          <MessageCircle className="h-4 w-4" />
          Chat (Unavailable)
        </button>
      );
    }

    return this.props.children;
  }
}

// Internal chat button component that uses the provider context
function ChatButtonInternal() {
  const { openChat, isReady } = useFrontChat();

  const handleChatClick = () => {
    if (isReady) {
      openChat();
    }
  };

  return (
    <>
      {isReady && (
        <button className="flex items-center gap-2 p-3" onClick={handleChatClick}>
          <MessageCircle className="h-4 w-4" />
          Start Support Chat
        </button>
      )}
    </>
  );
}

// Main component that wraps with error boundary only (provider is now at app level)
export function FrontChatButton() {
  return (
    <FrontChatErrorBoundary>
      <ChatButtonInternal />
    </FrontChatErrorBoundary>
  );
}
